import React, { useEffect, useState } from "react";
import PageHeader from "../../../../components/heading";
import { useTranslation } from "react-i18next";
import {
  PagesByFolderQueryHookResult,
  useDeletePage2Mutation,
  usePagesByFolderQuery,
} from "../../../../graphql/projects.hooks";
import { useLocation, useParams } from "react-router-dom";
import Pagination from "../../../../components/pagination";
import SearchBar from "../../../../components/searchBar";
import FolderPageListGrid from "../FolderPageListGrid";
import ImgFolder from "../img/folder.svg";
import ImgPage from "../img/test-page.png";
import FolderPageListTable from "../FolderPageListTable";
import { useDebounceEffect } from "../../../../hooks/useDebounceEffect";
import NoPages from "../noPages";
import useGetUrlLocale from "../../../../hooks/useGetUrlLocale";
import { Spinner } from "react-bootstrap";
import { useIsNoShared } from "../../../../hooks/useIsNoShared";
import { IMoveItems } from "../../../../types";
import { useReactiveVar } from "@apollo/client";
import { isLoadingOfDeletion, moveItem } from "../../../../graphql/state";
import DeletePageFolderModal from "../../../../components/modals/deletePageFolderModal";
import { useMassDeleteFolderAndPageMutation } from "../../../../graphql/page.hooks";
import { NotFoundPlug } from "../../../../components/plugs/notFound/NotFoundPlug";
import { ITEMS } from "../../../../utils";

interface FolderPageProps {}
export type DomainFolderProps =
  PagesByFolderQueryHookResult["data"]["pagesByFolder"]["folder"]["accessFolderDomainByPriority"];

const FolderPage: React.FC<FolderPageProps> = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const projectSettings = JSON.parse(localStorage.getItem("mePageSettings"));
  const [currentPage, setCurrentPage] = useState<number>(
    projectSettings?.folderPage?.currentPage || 1
  );
  const [limit, setLimit] = useState<number>(
    projectSettings?.folderPage?.limit || 10
  );
  const { id } = useParams<{ id: string; projectId: string }>();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [viewMode, setViewMode] = useState<"grid" | "table">(
    projectSettings?.folderPage?.viewMode || "grid"
  );

  const {
    data: PagesByFolder,
    loading,
    refetch,
  } = usePagesByFolderQuery({
    variables: {
      page: currentPage,
      folderId: Number(id),
      limit,
      search: searchTerm || null,
    },
    fetchPolicy: "network-only",
  });

  const [massDelete] = useMassDeleteFolderAndPageMutation({
    onCompleted: () => {
      setTimeout(async () => {
        await refetch();
        isLoadingOfDeletion(false);
      }, 1000);
    },
  });

  const [selectedItems, setSelectedItems] = useState<IMoveItems>({
    pageIds: [],
    folderIds: [],
  });
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const moveItemState = useReactiveVar(moveItem);
  let deleteModalTitle = t("modal.deleteFiles");
  let deleteModalDescription = t("modal.deleteFilesText");
  let deleteModalAdditionText = t("projects.permanentlyFolder");

  if (selectedItems.folderIds.length === 1 && !selectedItems.pageIds.length) {
    deleteModalTitle = t("projects.deleteFolder");
    deleteModalDescription = t("projects.deleteFolderDescription");
  } else if (
    selectedItems.pageIds.length === 1 &&
    !selectedItems.folderIds.length
  ) {
    deleteModalTitle = t("profile.deleteAction");
    deleteModalDescription = t("projects.deletePageDescription");
    deleteModalAdditionText = t("projects.permanently");
  }

  const tryDeleteFiles = async () => {
    try {
      const { data } = await massDelete({ variables: selectedItems });

      if (data?.massDeleteFolderAndPage) {
        setShowDeleteModal(false);
        moveItem({ ...moveItemState, data: { folderIds: [], pageIds: [] } });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const settings = JSON.parse(localStorage.getItem("mePageSettings"));
    localStorage.setItem(
      "mePageSettings",
      JSON.stringify({
        ...settings,
        folderPage: { viewMode, limit, currentPage },
      })
    );
  }, [viewMode, limit, currentPage]);

  useEffect(() => {
    if (
      !moveItemState.data.folderIds.length &&
      !moveItemState.data.pageIds.length
    ) {
      setSelectedItems({ folderIds: [], pageIds: [] });
    }
  }, [moveItemState.data]);

  useEffect(() => {
    document.title = "Folder Page";
  }, []);

  const handleSearch = () => {
    refetch({
      search: searchTerm,
      page: currentPage,
      folderId: Number(id),
      limit,
    });
  };
  const [deletePage] = useDeletePage2Mutation({
    onCompleted: () => {
      refetch();
    },
  });
  useDebounceEffect(
    () => {
      handleSearch();
    },
    1000,
    [searchTerm]
  );

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(event.target.value);
    setLimit(newLimit);
    setCurrentPage(1);
    refetch({
      search: searchTerm,
      page: 1,
      folderId: Number(id),
      limit: newLimit,
    });
  };

  const pagination = PagesByFolder?.pagesByFolder?.paginator;
  const pageCount = pagination?.totalPages || 0;
  const totalItems = pagination?.totalItemCount || 0;
  const firstItemIndex = pagination
    ? (currentPage - 1) * pagination.itemNumberPerPage + 1
    : 0;
  const lastItemIndex = pagination
    ? Math.min(currentPage * pagination.itemNumberPerPage, totalItems)
    : 0;

  const domain = PagesByFolder?.pagesByFolder?.folder?.domain;

  const foldersInFolder = PagesByFolder?.pagesByFolder;
  const parentFolder = foldersInFolder?.folder;

  const parentFolderId = PagesByFolder?.pagesByFolder?.folder?.parentFolder?.id;

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const lng = useGetUrlLocale();

  const [folderTitle, setFolderTitle] = useState<string>("");
  const projectAccessType: DomainFolderProps =
    PagesByFolder?.pagesByFolder?.folder?.accessFolderDomainByPriority;

  const folderAccessType = parentFolder?.userAccesses;

  useEffect(() => {
    const newTitle = PagesByFolder?.pagesByFolder?.folder?.title;
    if (newTitle && !folderTitle) {
      setFolderTitle(newTitle);
    }
  }, [foldersInFolder, folderTitle, PagesByFolder]);
  const isNoShared = useIsNoShared();
  const sharedValue = location.state?.shared;

  useEffect(() => {
    if (
      !foldersInFolder?.pages.length &&
      !foldersInFolder?.folders.length &&
      !loading
    ) {
      setCurrentPage(1);
    }
  }, [foldersInFolder?.pages, foldersInFolder?.folders, loading]);

  const link =
    (!isNoShared || sharedValue) &&
    projectAccessType !== 0 &&
    projectAccessType !== undefined
      ? folderAccessType.length !== 0
        ? `${lng}/shared`
        : parentFolderId
          ? `${lng}/projects/${domain?.id}/folder/${parentFolderId}`
          : `${lng}/projects/${domain?.id}`
      : parentFolderId
        ? `${lng}/projects/${domain?.id}/folder/${parentFolderId}`
        : `${lng}/projects/${domain?.id}`;

  return (
    <>
      <PageHeader
        projectAccessType={projectAccessType}
        title={parentFolder?.title || folderTitle}
        paths={
          !isNoShared || sharedValue
            ? [
                { label: t("menu.shared"), path: `${lng}/shared` },

                ...(folderAccessType?.length === 0 && isNoShared
                  ? [
                      {
                        label: domain?.title,
                        path: `${lng}/projects/${domain?.id}`,
                      },
                    ]
                  : []),
                {
                  label: parentFolder?.title,
                  path: `${lng}/projects/${domain?.id}/folder/${id}`,
                },
              ]
            : [
                { label: t("projects.main"), path: `${lng}/projects` },
                { label: domain?.title, path: `${lng}/projects/${domain?.id}` },
                {
                  label: parentFolder?.title,
                  path: `${lng}/projects/${domain?.id}/folder/${id}`,
                },
              ]
        }
      />

      <SearchBar
        t={t}
        title={parentFolder?.title || folderTitle}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        viewMode={viewMode}
        setViewMode={setViewMode}
        handleSearch={handleSearch}
        link={link}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
        setShowDeleteModal={setShowDeleteModal}
        isDisabled={
          !(
            foldersInFolder?.folders?.length > 0 ||
            foldersInFolder?.pages?.length
          )
        }
      />
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" role="status" />
        </div>
      ) : foldersInFolder?.folders?.length > 0 ||
        foldersInFolder?.pages?.length > 0 ? (
        <>
          {viewMode === "grid" ? (
            <FolderPageListGrid
              folders={foldersInFolder?.folders || []}
              pages={foldersInFolder?.pages}
              folderImgSrc={ImgFolder}
              pageImgSrc={ImgPage}
              domainId={domain?.id}
              loading={loading}
              deletePage={deletePage}
              projectAccessType={folderAccessType}
              folderPageAccessType={projectAccessType}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              refetchPagesByDomain={refetch}
            />
          ) : (
            <FolderPageListTable
              folders={foldersInFolder?.folders || []}
              pages={foldersInFolder?.pages || []}
              folderImgSrc={ImgFolder}
              pageImgSrc={ImgPage}
              domainId={domain?.id}
              deletePage={deletePage}
              projectAccessType={folderAccessType}
              folderPageAccessType={projectAccessType}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              refetchPagesByDomain={refetch}
            />
          )}

          <Pagination
            t={t}
            pages={pageCount}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            firstItemIndex={firstItemIndex}
            lastItemIndex={lastItemIndex}
            totalItems={totalItems}
            limit={limit}
            onLimitChange={handleLimitChange}
          />
        </>
      ) : searchTerm ? (
        <NotFoundPlug />
      ) : (
        <NoPages domainId={String(domain?.id)} folderId={id} />
      )}
      <DeletePageFolderModal
        itemType={ITEMS.FILES}
        title={deleteModalTitle}
        description={deleteModalDescription}
        additionText={deleteModalAdditionText}
        showDeleteModal={showDeleteModal}
        handleCloseModal={() => setShowDeleteModal(false)}
        handleConfirmDelete={tryDeleteFiles}
      />
    </>
  );
};

export default FolderPage;
